import React, { useState, useEffect, useMemo } from 'react';
import { Avatar, Box, Stack } from '@mui/material';
import BtnPrimary from '../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../component/SecondaryButton';
import ActionDialog from '../../../../../../component/ActionDialog';
import SectionLoader from '../../../../../../component/SectionLoader';
import EmptyState from '../../../../../../component/EmptyState';
import { StatusChip } from '../../../../../../component/listing';
import { approveExpense, getExpenseById, rejectExpense } from '../../../../../../services/expense';
import { fetchEmployeesByIdArray } from '../../../../../../services/employee';
import { useNotify } from '../../../../../../component/NotificationProvider';
import Topbar from "../../../../../../component/TopBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from 'react-router';
import { tokens, radius } from '../../../../../../theme';

const STATUS_TONE = { pending: "warning", approved: "success", rejected: "danger" };
const initialsOf = (n) => (n || "?").split(" ").filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase();
const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

const Field = ({ label, children }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "0.5rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, wordBreak: "break-word" }}>{children ?? "—"}</Box>
  </Box>
);

const ExpenseRequestDetails = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [expenseData, setExpenseData] = useState(null);
  const [empById, setEmpById] = useState({});
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getExpenseById(id);
        const data = res?.data || null;
        if (!active) return;
        setExpenseData(data);
        const ids = [data?.submitterId, ...(data?.policy?.approverIds || [])].filter(Boolean);
        if (ids.length) {
          const empRes = await fetchEmployeesByIdArray(ids);
          if (!active) return;
          const m = {}; (empRes?.data || []).forEach((e) => { m[e._id] = e; });
          setEmpById(m);
        }
      } catch (error) {
        if (active) notify.error(error?.message || "Failed to load expense details");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitter = expenseData ? empById[expenseData.submitterId] : null;
  const name = submitter ? `${submitter.fname || ""} ${submitter.lname || ""}`.trim() : "Unknown";
  const approvers = useMemo(
    () => (expenseData?.policy?.approverIds || []).map((aid) => empById[aid]).filter(Boolean),
    [expenseData, empById]
  );

  if (loading) return <Box sx={{ width: "100%", paddingBlock: "1rem" }}><Topbar MainHeading={"Expense"} /><SectionLoader minHeight="50vh" /></Box>;
  if (!expenseData) return <Box sx={{ width: "100%", paddingBlock: "1rem" }}><Topbar MainHeading={"Expense"} /><EmptyState title="Expense not found" /></Box>;

  const isPending = expenseData.status === "pending";
  const cardSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.25rem" };

  return (
    <Box sx={{ width: "100%" }}>
      <Topbar MainHeading={"Expense"} breadcrumbs={[{ label: "Expense", href: "" }, { label: "Dashboard", href: "/app/expense-dashboard" }, { label: "Request" }]} />

      <Box sx={{ mb: "1rem" }}>
        <BtnSecondary onClick={() => navigate(-1)} sx={{ borderRadius: "8px" }}>
          <ArrowBackIcon sx={{ fontSize: "1.1rem", mr: 0.5 }} /> Back
        </BtnSecondary>
      </Box>

      <Box sx={cardSx}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar src={submitter?.profileImage || undefined} sx={{ width: "3rem", height: "3rem", bgcolor: tokens.accent, fontWeight: 600 }}>{initialsOf(name)}</Avatar>
            <Box>
              <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text }}>{name}</Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{submitter?.aliasId || "—"}</Box>
            </Box>
          </Stack>
          <StatusChip label={expenseData.status} tone={STATUS_TONE[expenseData.status]} />
        </Stack>
      </Box>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start">
        <Box sx={{ ...cardSx, flex: 2, width: "100%" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: "1.5rem" }}>
            <Field label="Category">{expenseData?.policyCateogary?.name}</Field>
            <Field label="Amount">{money(expenseData?.amount)}</Field>
            <Field label="Date">{new Date(expenseData?.createdDate || expenseData?.createdAt).toLocaleString('en-GB')}</Field>
            <Field label="Request ID">{expenseData?._id}</Field>
          </Box>
          <Box sx={{ mt: "1.5rem", pt: "1.25rem", borderTop: `1px solid ${tokens.border}` }}>
            <Field label="Description"><Box sx={{ fontWeight: 400 }}>{expenseData?.reason || "—"}</Box></Field>
          </Box>
        </Box>

        <Box sx={{ ...cardSx, flex: 1, width: "100%" }}>
          <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mb: "1rem" }}>Approvers</Box>
          {approvers.length === 0 ? (
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>No approvers configured.</Box>
          ) : (
            <Stack spacing={1}>
              {approvers.map((a) => (
                <Stack key={a._id} direction="row" alignItems="center" spacing={1.25} sx={{ p: "0.75rem 0.75rem", border: `1px solid ${tokens.border}`, borderRadius: "10px" }}>
                  <Avatar sx={{ width: "1.75rem", height: "1.75rem", bgcolor: tokens.accent, fontSize: "0.8125rem", fontWeight: 600 }}>{initialsOf(`${a.fname} ${a.lname}`)}</Avatar>
                  <Box sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{`${a.fname || ""} ${a.lname || ""}`.trim()}</Box>
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>

      {isPending && (
        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          <BtnSecondary onClick={() => setRejectDialogOpen(true)} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder }}>Reject</BtnSecondary>
          <BtnPrimary onClick={() => setApproveDialogOpen(true)} sx={{ borderRadius: "8px" }}>Approve</BtnPrimary>
        </Stack>
      )}

      <ActionDialog type="approve" open={approveDialogOpen} onClose={() => { setApproveDialogOpen(false); setRefresh((p) => !p); }} reviewApi={approveExpense} id={id} text="Approve Expense?" />
      <ActionDialog type="reject" open={rejectDialogOpen} onClose={() => { setRejectDialogOpen(false); setRefresh((p) => !p); }} reviewApi={rejectExpense} id={id} text="Reject Expense?" />
    </Box>
  );
};

export default ExpenseRequestDetails;
